import { Component, ChangeDetectionStrategy, OnInit, signal, DestroyRef, inject } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { debounceTime, distinctUntilChanged, filter, finalize, switchMap, take, tap } from 'rxjs/operators';
import { IrisFoldersService } from '@iris/common/services/docs/folders.service';
import { TranslatePipe } from '@ngx-translate/core';
import { StrakitButtonComponent, StrakitTextComponent } from '@ngx-strakit/cdk/controls';
import { MatInputModule } from '@angular/material/input';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable } from 'rxjs';
import { IrisLoaderModule } from '@iris/common/modules/loader';
import {
  ConfirmByKeypressDirective,
  SkModalBase,
  SkModalContentComponent,
  StrakitButtonPrefixDirective,
} from '@ngx-strakit/cdk';

@Component({
  selector: 'iris-dms-folder-rename-modal',
  imports: [
    TranslatePipe,
    ReactiveFormsModule,
    ConfirmByKeypressDirective,

    MatInputModule,

    IrisLoaderModule,

    SkModalContentComponent,
    StrakitTextComponent,
    StrakitButtonComponent,
    StrakitButtonPrefixDirective,
  ],
  templateUrl: './folder-rename-modal.component.html',
  styleUrls: ['./folder-rename-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IrisDmsFolderRenameModalComponent extends SkModalBase<IrisDmsFolderRenameModalComponent, string> implements OnInit {
  private readonly dmsFolderService = inject(IrisFoldersService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly destoryRef = inject(DestroyRef);
  readonly data = inject<{
    folderName: string;
    folderId: string;
  }>(MAT_DIALOG_DATA);

  readonly editFolderForm: FormGroup = this.formBuilder.group({
    name: new FormControl<string>(this.data.folderName, [Validators.required]),
  });

  readonly duplicateChecking = signal(false);

  get name(): AbstractControl<string> {
    return this.editFolderForm.get('name');
  }

  ngOnInit(): void {
    this.handleNameChanged();
  }

  renameFolder(): void {
    this.closeWithResult(this.name.value);
  }

  private handleNameChanged(): void {
    this.name.valueChanges
      .pipe(
        filter(Boolean),
        distinctUntilChanged(),
        debounceTime(500),
        switchMap(() => this.checkIfFolderNameExist()),
        takeUntilDestroyed(this.destoryRef),
      ).subscribe();
  }

  private checkIfFolderNameExist(): Observable<boolean> {
    this.duplicateChecking.set(true);
    return this.dmsFolderService.checkFolderExists(this.data.folderId, this.name.value).pipe(
      take(1),
      tap(exists => {
        if (exists) {
          this.name.setErrors({ 'exists': true });
          this.name.markAsTouched();
        } else {
          this.name.setErrors(null);
        }
      }),
      finalize(() => this.duplicateChecking.set(false)),
    );
  }
}
